import {
  Category,
  ICartProduct,
  ISearchProduct,
  PBMProgram,
  PBMSalePrice,
} from '@/types';
import isEmpty from 'lodash/isEmpty';
import { getPromotionalPackDetails } from './cart.utils';

function categoryIdsFromProduct(categories?: Category[]): number[] {
  if (!categories?.length) return [];
  return categories
    .map((c) => Number(c.id))
    .filter((id) => Number.isFinite(id));
}

interface Variation {
  id: string | number;
  title: string;
  price: number;
  sale_price?: number;
  quantity: number;
  in_stock: number;
  [key: string]: unknown;
}
export function generateCartItem(
  product_item: ISearchProduct & {
    pbm_flag?: boolean;
    pbm_sale_price_id?: number;
    pbm_cpf?: string;
    pbm_quantity?: number;
    pbm_program?: PBMProgram;
    pbm_promotion?: PBMSalePrice;
  },
  variation?: Variation,
): ICartProduct {
  const item = getPromotionalPackDetails(product_item).product;
  const category_ids = categoryIdsFromProduct(
    (product_item as { categories?: Category[] }).categories ?? item.categories,
  );

  const type_idsFromCatalog = (): string[] | undefined => {
    if (item.type_ids?.length) {
      return item.type_ids.map((id) => String(id));
    }
    const fromTypesId = (item as { types_id?: number[] }).types_id;
    if (fromTypesId?.length) {
      return fromTypesId.map((id) => String(id));
    }
    const fromTypes = (item as { types?: { id?: string | number }[] }).types;
    if (fromTypes?.length) {
      return fromTypes
        .map((t) => (t.id != null ? String(t.id) : ''))
        .filter(Boolean);
    }
    if (item.type_id != null && item.type_id !== '') {
      return [String(item.type_id)];
    }
    return undefined;
  };
  const resolvedTypeIds = type_idsFromCatalog();
  const typesIdNumeric = (): number[] | undefined => {
    const raw = (item as { types_id?: number[] }).types_id;
    if (raw?.length) {
      return raw.map((n) => Number(n)).filter((n) => Number.isFinite(n));
    }
    if (resolvedTypeIds?.length) {
      return resolvedTypeIds
        .map((s) => Number(s))
        .filter((n) => Number.isFinite(n));
    }
    return undefined;
  };
  const resolvedTypesIdNum = typesIdNumeric();

  const {
    id,
    product_id,
    name,
    slug,
    image,
    price,
    sale_price,
    quantity,
    unit,
    is_digital,
    language,
    in_flash_sale,
    shop,
    type_id,
    sku,
    characteristics = [],
    product_prices = [],
    promotional_pack_unit_price,
    promotional_pack_quantity,
    is_promotional_pack,
    cashback_price,
  } = item;

  if (!isEmpty(variation)) {
    const isPBM = !!product_item.pbm_flag;
    // For PBM, use product_item.sale_price if present, else fallback to variation.sale_price or variation.price
    const finalSalePrice = isPBM
      ? typeof product_item.sale_price === 'number'
        ? product_item.sale_price
        : variation.sale_price
          ? variation.sale_price
          : variation.price
      : variation.sale_price
        ? variation.sale_price
        : variation.price;
    const itemTotal = finalSalePrice * (product_item.quantity ?? 1);
    return {
      id: `${id}.${variation.id}`,
      product_id: product_id,
      sku,
      type_id,
      ...(resolvedTypeIds?.length ? { type_ids: resolvedTypeIds } : {}),
      ...(resolvedTypesIdNum?.length ? { types_id: resolvedTypesIdNum } : {}),
      name: `${name} - ${variation.title}`,
      slug,
      unit,
      is_digital: variation?.is_digital as boolean | undefined,
      stock: variation.quantity,
      price: Number(variation.price),
      sale_price: finalSalePrice,
      calculated_sale_price: finalSalePrice,
      image: image?.thumbnail,
      variationId: variation.id,
      language,
      in_flash_sale,
      shop_id: shop.id,
      product_prices,
      promotional_pack_unit_price,
      promotional_pack_quantity,
      is_promotional_pack,
      cashback_price,
      category: item.category,
      category_ids: category_ids.length ? category_ids : undefined,
      brand: item.manufacturer || item.brand,
      pbm_info: product_item.pbm_flag
        ? {
            flag: true,
            sale_price_id: product_item.pbm_sale_price_id || 0,
            cpf: product_item.pbm_cpf || '',
            quantity: product_item.pbm_quantity || 0,
            program:
              product_item.pbm_program &&
              typeof product_item.pbm_program === 'object'
                ? product_item.pbm_program
                : undefined,
            promotion: product_item.pbm_promotion,
          }
        : undefined,
    };
  }

  const isPBM = !!product_item.pbm_flag;
  // For PBM, use product_item.sale_price if present, else fallback to sale_price
  const finalSalePrice = isPBM
    ? typeof product_item.sale_price === 'number'
      ? product_item.sale_price
      : typeof sale_price === 'number'
        ? sale_price
        : price
    : typeof sale_price === 'number'
      ? sale_price
      : price;
  const itemTotal = finalSalePrice * (product_item.quantity ?? 1);
  return {
    id,
    name,
    product_id,
    sku,
    type_id,
    ...(resolvedTypeIds?.length ? { type_ids: resolvedTypeIds } : {}),
    ...(resolvedTypesIdNum?.length ? { types_id: resolvedTypesIdNum } : {}),
    slug,
    unit,
    is_digital,
    image: image?.thumbnail,
    stock: item.quantity,
    price,
    sale_price: finalSalePrice,
    calculated_sale_price: finalSalePrice,
    product_prices,
    language,
    in_flash_sale,
    shop_id: shop?.id,
    characteristics,
    promotional_pack_unit_price,
    promotional_pack_quantity,
    is_promotional_pack,
    cashback_price,
    category: item.category,
    category_ids: category_ids.length ? category_ids : undefined,
    brand: item.manufacturer || item.brand,
    pbm_info: product_item.pbm_flag
      ? {
          flag: true,
          sale_price_id: product_item.pbm_sale_price_id || 0,
          cpf: product_item.pbm_cpf || '',
          quantity: product_item.pbm_quantity || 0,
          program:
            product_item.pbm_program &&
            typeof product_item.pbm_program === 'object'
              ? product_item.pbm_program
              : undefined,
          promotion: product_item.pbm_promotion,
        }
      : undefined,
  };
}
